// SportLimitsEditor (BPG-5) — dumb-broker per-game MAX BET editor for a player.
// Four cards: 🎰 Casino (one max bet for all non-API games) + 🏏/⚽/🎾 each with a
// single "Max bet" and an Advanced expander for the detailed caps (match-odds /
// bookmaker / session-fancy / per-match exposure). Uses the per-sport route mounted
// on BOTH broker apps (admin → any player; agent → own direct player, server-scoped):
//   GET  /admin/cricket-report/player-limits/:userId → { perSport, categories }
//   POST /admin/cricket-report/player-limits/:userId  { perSport:{...}, casinoMaxBet }
// The simple "Max bet" sets all three stake caps for that sport at once; Advanced
// splits them. Blank = no cap, 0 = blocked.

import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type Caps = { max_stake_match_odds: number | null; max_stake_bookmaker: number | null; max_stake_fancy: number | null; max_match_exposure: number | null };
type PerSport = { cricket: Caps | null; football: Caps | null; tennis: Caps | null };
const SPORTS: { key: keyof PerSport; label: string }[] = [
  { key: 'cricket', label: '🏏 Cricket' }, { key: 'football', label: '⚽ Football' }, { key: 'tennis', label: '🎾 Tennis' },
];
const numOrBlank = (v: any) => (v == null ? '' : String(v));

export default function SportLimitsEditor({ userId, onSaved }: { userId: number; onSaved?: () => void }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [adv, setAdv] = useState<Record<string, boolean>>({});
  // draft per sport: { max, mo, bm, fancy, exp }
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});
  const [casino, setCasino] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<any>(`/admin/cricket-report/player-limits/${userId}`);
      const d = r.data ?? r;
      const ps: PerSport = d.perSport || { cricket: null, football: null, tennis: null };
      const nd: Record<string, Record<string, string>> = {};
      const advInit: Record<string, boolean> = {};
      for (const { key } of SPORTS) {
        const c = ps[key];
        // "Max bet" seeds from match-odds cap (the simple single value).
        nd[key] = {
          max: numOrBlank(c?.max_stake_match_odds),
          mo: numOrBlank(c?.max_stake_match_odds), bm: numOrBlank(c?.max_stake_bookmaker),
          fancy: numOrBlank(c?.max_stake_fancy), exp: numOrBlank(c?.max_match_exposure),
        };
        // SAFETY (2026-08-05 audit): if the per-market caps DIFFER from the
        // single match-odds value, force this card to Advanced. Otherwise Simple
        // mode would hide the tighter bookmaker/fancy caps AND silently flatten
        // them up to the match-odds value on the next save. Simple mode is only
        // safe when all three stake caps are equal (flatten is a no-op there).
        const mo = c?.max_stake_match_odds, bm = c?.max_stake_bookmaker, fy = c?.max_stake_fancy;
        if ((bm != null && bm !== mo) || (fy != null && fy !== mo)) advInit[key] = true;
      }
      setDraft(nd);
      setAdv(advInit);
      setCasino(numOrBlank(d.categories?.casino));
    } catch (e) { setErr((e as ApiError).message || 'Failed to load limits.'); }
    finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { void load(); }, [load]);

  const num = (s: string) => (s === '' ? '' : Math.max(0, Math.floor(Number(s) || 0)));

  async function saveSport(key: keyof PerSport) {
    if (savingKey) return;
    setSavingKey(key); setErr(null); setNote(null);
    const d = draft[key] || {};
    const useAdv = adv[key];
    // Simple mode: one Max bet → all three stake caps. Advanced: each cap on its own.
    const caps = useAdv
      ? { maxStakeMatchOdds: num(d.mo), maxStakeBookmaker: num(d.bm), maxStakeFancy: num(d.fancy), maxMatchExposure: num(d.exp) }
      : { maxStakeMatchOdds: num(d.max), maxStakeBookmaker: num(d.max), maxStakeFancy: num(d.max), maxMatchExposure: num(d.exp) };
    try {
      await adminApi.post(`/admin/cricket-report/player-limits/${userId}`, { perSport: { [key]: caps } });
      setNote(`${key} max bet saved ✓`); onSaved?.(); await load();
    } catch (e) { setErr((e as ApiError).message || 'Failed to save.'); }
    finally { setSavingKey(null); }
  }

  async function saveCasino() {
    if (savingKey) return;
    setSavingKey('casino'); setErr(null); setNote(null);
    const v = num(casino);
    try {
      // One Casino max bet governs every non-cricket game (BPG-6 single bucket).
      await adminApi.post(`/admin/cricket-report/player-limits/${userId}`, { casinoMaxBet: v });
      setNote('Casino max bet saved ✓'); onSaved?.(); await load();
    } catch (e) { setErr((e as ApiError).message || 'Failed to save.'); }
    finally { setSavingKey(null); }
  }

  if (loading) return <div className="text-[12.5px] text-neutral-500">Loading max-bet limits…</div>;

  const cell = 'w-24 rounded-lg border border-neutral-300 dark:border-white/15 bg-white dark:bg-white/5 px-2.5 py-1.5 text-[14px] text-right font-mono text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50';

  return (
    <div className="space-y-3">
      <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white">💰 Max bet — per game</h3>
      <p className="text-[11px] text-neutral-500">Biggest single bet on each game. Blank = no limit · 0 = blocked.</p>

      {/* 🎰 Casino */}
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-white/10 p-2.5">
        <span className="w-24 text-[13px] font-semibold">🎰 Casino</span>
        <div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400">₹</span>
          <input type="number" min={0} step={100} value={casino} placeholder="no limit"
            onChange={(e) => { setCasino(e.target.value); setNote(null); }} className={`${cell} pl-6`} /></div>
        <button type="button" disabled={savingKey === 'casino'} onClick={() => void saveCasino()}
          className="rounded-lg bg-violet-600 px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-40 hover:bg-violet-700">{savingKey === 'casino' ? '…' : 'Save'}</button>
        <span className="text-[10.5px] text-neutral-400">all other games</span>
      </div>

      {/* per game */}
      {SPORTS.map(({ key, label }) => (
        <div key={key} className="rounded-lg border border-neutral-200 dark:border-white/10 p-2.5 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-24 text-[13px] font-semibold">{label}</span>
            {!adv[key] ? (
              <div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400">₹</span>
                <input type="number" min={0} step={100} value={draft[key]?.max ?? ''} placeholder="no limit"
                  onChange={(e) => { setDraft((s) => ({ ...s, [key]: { ...s[key], max: e.target.value } })); setNote(null); }} className={`${cell} pl-6`} /></div>
            ) : <span className="text-[11px] text-neutral-500 flex-1">detailed caps below</span>}
            <button type="button" disabled={savingKey === key} onClick={() => void saveSport(key)}
              className="rounded-lg bg-violet-600 px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-40 hover:bg-violet-700">{savingKey === key ? '…' : 'Save'}</button>
            <button type="button" onClick={() => setAdv((a) => ({ ...a, [key]: !a[key] }))}
              className="text-[10.5px] font-semibold text-emerald-700 dark:text-emerald-300 hover:underline">{adv[key] ? 'Simple' : 'Advanced'}</button>
          </div>
          {adv[key] && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {([['mo', 'Match odds'], ['bm', 'Bookmaker'], ['fancy', 'Session/Fancy'], ['exp', 'Per-match exposure']] as const).map(([k, lbl]) => (
                  <label key={k} className="text-[11px] text-neutral-600 dark:text-neutral-300">{lbl}
                    <div className="relative mt-0.5"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400">₹</span>
                      <input type="number" min={0} step={100} value={draft[key]?.[k] ?? ''} placeholder="no limit"
                        onChange={(e) => { setDraft((s) => ({ ...s, [key]: { ...s[key], [k]: e.target.value } })); setNote(null); }} className={`${cell} w-full pl-6`} /></div>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-amber-600 dark:text-amber-400">These detailed caps apply to <b>{key}</b> only.</p>
            </>
          )}
          {!adv[key] && <p className="text-[10px] text-neutral-400">one max bet for {key} · tap Advanced for per-market caps</p>}
        </div>
      ))}
      {note && <div className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-300">{note}</div>}
      {err && <div className="text-[12px] font-semibold text-rose-600 dark:text-rose-400">{err}</div>}
    </div>
  );
}
